import {BehaviorSubject, combineLatest, defer, forkJoin, from, interval, merge, of, Subject} from 'rxjs';
import {delay, map, startWith, tap, switchMap, shareReplay, take} from 'rxjs/operators';
import {mc} from '@/core';
import {assetsApi} from '@/api/assets';
import {contractApi} from '@/api/contract';
import {browser$} from '@/streams/browser';
import {contracts$} from '@/streams/config/contract';
import {genAccountOptions} from '@/modules/assets/util';
import {currencies$} from '@/streams/config/currency';

const quotationNotifier$ = new Subject();

export const QUOTATION_STATES = Object.freeze({
  INITIALIZED: 1,
  SUCCESS: 2,
});

export function dispatchQuotation(fromCurrency, toCurrency, fromAmountEv) {
  const params = {
    fromCurrency,
    toCurrency,
    fromAmountEv,
  };
  quotationNotifier$.next(params);
}

export const convertQuotation$ = quotationNotifier$.pipe(
  switchMap((quotationParams) => from(assetsApi.getConversionQuote(quotationParams)).pipe(tap(showErrorMessage), switchMap(generateResetStateStream))),
  startWith({data: generateQuotationDefaultData(), timeout: 0})
);

export const canPtTransfer$ = new BehaviorSubject(true);

export const contractOptions$ = combineLatest([contracts$, canPtTransfer$]).pipe(map(([o, canPtTransfer]) => genAccountOptions(o, {hasPtCurrency: canPtTransfer, isPilotContract: false})));

function generateQuotationDefaultData() {
  return {
    quoteArgs: {
      price: '--',
      proceeds: '--',
      ttlMs: 7000,
    },
  };
}

function generateResetStateStream(response) {
  const data = response.data;
  if (data) {
    const {ttlMs = 7000} = data.quoteArgs || {};
    const count = Math.floor(ttlMs / 1000);
    return merge(
      of({data, timeout: count}),
      interval(1000).pipe(
        map((num) => ({data, timeout: count - num - 1})),
        take(count - 1)
      ),
      of({data: generateQuotationDefaultData(), timeout: 0}).pipe(delay(count * 1000))
    );
  }
  return of({data: generateQuotationDefaultData(), timeout: 0});
}

export function showErrorMessage(response) {
  const error = response.error;
  if (error) {
    mc.send('toast', 'error', error);
  }
}

const contractConvertUSDCurrencies$ = defer(() => contractApi.getContractTransferCurrencies()).pipe(
  tap(showErrorMessage),
  map((response) => response.data || []),
  shareReplay(1)
);

const convertCurrencies$ = defer(() => assetsApi.getConversionSupportedCurrencies({cfgType: 'CFG_CURRENCY_PAIR'})).pipe(
  tap(showErrorMessage),
  map((response) => {
    if (response.data) {
      return response.data.cfgList || [];
    }
    return [];
  })
);
export const contractTransferCurrencies$ = browser$.pipe(
  switchMap(() => forkJoin([contractConvertUSDCurrencies$, convertCurrencies$])),
  map(resolveTransferCurrencies),
  startWith({
    convertUSDCurrencies: [],
    currencyMappings: {},
    options: [],
  })
);

export const currencyBasic$ = currencies$.pipe(
  map((currencies) =>
    currencies.reduce((preValue, currencyItem) => {
      preValue[currencyItem.currency] = currencyItem;
      return preValue;
    }, {})
  ),
  shareReplay(1)
);

function resolveTransferCurrencies([contractConvertUSDCurrencies, convertCurrencies]) {
  return convertCurrencies.reduce(
    (preValue, item) => {
      const {fromCurrency, toCurrency} = item;
      if (contractConvertUSDCurrencies.indexOf(fromCurrency) > -1 && toCurrency === 'USD') {
        preValue.convertUSDCurrencies.push(fromCurrency);
        preValue.currencyMappings[fromCurrency] = item;
        preValue.options.push({
          value: fromCurrency,
          text: fromCurrency,
        });
      }
      return preValue;
    },
    {convertUSDCurrencies: [], currencyMappings: {}, options: []}
  );
}
